import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personAssociateSchema from '../../schema/personAssociateSchema';
import { DEFAULT_VALUES, DICT_SELECT_PROPS } from '../../constants/personAssociateFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonAssociate, updatePersonAssociate } from '../../services/userService';
import AddPersonDialog from './AddPersonDialog';

const PersonAssociateDialog = ({ open, onClose, personId, associate = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);
  const isEditMode = Boolean(associate?.ptsAssociateID);

  const methods = useForm({
    resolver: CreateFormResolver(personAssociateSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(associate || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { associateRelationshipOptions } = useUserDictionaryOptions();
  const ptsAssociateID = useWatch({ control: methods.control, name: 'ptsAssociateID' });

  const handlePersonCreated = useCallback(
    (person) => {
      methods.setValue('ptsAssociateID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPersonDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPersonID: personId,
        ptsAssociateID: data.ptsAssociateID,
        Relationship: data.Relationship,
        RelationshipForAssociate: data.RelationshipForAssociate || null,
        IsKeepSeparate: data.IsKeepSeparate || false,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => {
          if (isEditMode) {
            payload.UpdatedBy = currentUser?.Username;
            return updatePersonAssociate(
              { ptsPersonID: personId, ptsAssociateID: associate.ptsAssociateID },
              payload
            );
          }

          payload.CreatedBy = currentUser?.Username;
          payload.UpdatedBy = currentUser?.Username;
          return createPersonAssociate(payload);
        },
        onSuccess: (savedAssociate) => {
          dispatch(
            notify(
              isEditMode ? 'Associate updated successfully' : 'Associate created successfully',
              'success'
            )
          );
          onSaved?.(savedAssociate);
        },
      });
    },
    [isEditMode, associate, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this associate. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Associate' : 'Add Associate'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!associate?.Created}
            created={associate?.Created}
            createdBy={associate?.CreatedBy}
            updated={associate?.Updated}
            updatedBy={associate?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchPersonAutoComplete
              name="ptsAssociateID"
              label="Associate"
              defaultPersonId={ptsAssociateID}
              disabled={isEditMode}
              showAddIcon={!isEditMode}
              showDetailsIcon={false}
              onAddIconClick={() => setAddPersonDialogOpen(true)}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Relationship"
              label="Relationship"
              options={associateRelationshipOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="RelationshipForAssociate"
              label="Relationship For Associated Person"
              options={associateRelationshipOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSCheckbox name="IsKeepSeparate" label="Keep Separate" />
          </Grid>
        </Grid>
      </FormProvider>

      {addPersonDialogOpen && (
        <AddPersonDialog
          open={addPersonDialogOpen}
          onClose={() => setAddPersonDialogOpen(false)}
          onPersonCreated={handlePersonCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonAssociateDialog;
